import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { apiClient } from '@/services/api';
import { FormCard, FormCardMessage } from '@/components/ui/FormCard';
import { BasicCheckbox } from '@/components/ui/BasicCheckbox';
import { TOURNAMENT_ROLES } from '@/constants';
import styles from './styles.module.scss';

interface TournamentSettingsData {
  predictionDeadlineBufferMinutes: number;
  manualCoefficients: boolean;
  includeQualificationStages: boolean;
}

interface TournamentSettingsFormProps {
  tournament: {
    id: number;
    predictionDeadlineBufferMinutes?: number;
    manualCoefficients?: boolean;
    includeQualificationStages?: boolean;
    userRole?: string;
  };
  onSuccess?: () => void;
  onCancel?: () => void;
  onArchived?: () => void;
}

export const TournamentSettingsForm: React.FC<TournamentSettingsFormProps> = ({
  tournament,
  onSuccess,
  onCancel,
  onArchived,
}) => {
  const { t } = useTranslation('tournament');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<FormCardMessage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const isHolder = tournament.userRole === TOURNAMENT_ROLES.HOLDER;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TournamentSettingsData>({
    defaultValues: {
      predictionDeadlineBufferMinutes: tournament.predictionDeadlineBufferMinutes ?? 120,
      manualCoefficients: tournament.manualCoefficients ?? false,
      includeQualificationStages: tournament.includeQualificationStages ?? true,
    },
  });

  useEffect(() => {
    reset({
      predictionDeadlineBufferMinutes: tournament.predictionDeadlineBufferMinutes ?? 120,
      manualCoefficients: tournament.manualCoefficients ?? false,
      includeQualificationStages: tournament.includeQualificationStages ?? true,
    });
  }, [tournament, reset]);

  const onSubmit = async (data: TournamentSettingsData) => {
    setMessage(null);
    setIsLoading(true);

    try {
      const response = await apiClient.updateTournamentSettings(tournament.id, {
        predictionDeadlineBufferMinutes: data.predictionDeadlineBufferMinutes,
        manualCoefficients: data.manualCoefficients,
        includeQualificationStages: data.includeQualificationStages,
      });

      if (response.data?.success) {
        setMessage({ type: 'success', text: t('settingsSavedSuccess') });
        onSuccess?.();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || t('errorSavingSettings');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchive = async () => {
    setIsArchiving(true);
    setMessage(null);

    try {
      const response = await apiClient.archiveTournament(tournament.id);

      if (response.data?.success) {
        queryClient.invalidateQueries({ queryKey: ['next-to-predict'] });
        queryClient.invalidateQueries({
          predicate: query => {
            const key = query.queryKey;
            return (
              Array.isArray(key) &&
              (key[0] === 'tournament' || key[0] === 'tournament-matches') &&
              String(key[1]) === String(tournament.id)
            );
          },
        });
        onArchived?.();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || t('errorArchivingTournament');
      setMessage({ type: 'error', text: errorMessage });
      setShowArchiveConfirm(false);
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <>
      <FormCard
        title={t('tournamentSettings')}
        subtitle={t('configureTournamentRules')}
        titleSize="sm"
        message={message}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.formFields}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>{t('predictionDeadlineLabel')}</label>
              <input
                className={classNames('field', { 'field--error': errors.predictionDeadlineBufferMinutes })}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                min={1}
                max={1440}
                disabled={isLoading}
                {...register('predictionDeadlineBufferMinutes', {
                  required: t('fieldRequired'),
                  min: { value: 1, message: t('mustBeGreaterThanZero') },
                  max: { value: 1440, message: t('mustBe1440OrLess') },
                  valueAsNumber: true,
                  onChange: (e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                  },
                })}
              />
              {errors.predictionDeadlineBufferMinutes && (
                <div className={styles.fieldError}>{errors.predictionDeadlineBufferMinutes.message}</div>
              )}
              <span className={styles.hint}>
                {t('predictionDeadlineHint')}
              </span>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.checkboxRow}>
                {(() => {
                  const { ref, ...rest } = register('manualCoefficients');
                  return (
                    <BasicCheckbox
                      id="manual-coefficients"
                      color="green"
                      disabled={isLoading}
                      innerRef={ref}
                      {...rest}
                    />
                  );
                })()}
                <label htmlFor="manual-coefficients" className={styles.label}>{t('manualCoefficientsLabel')}</label>
              </div>
              <span className={styles.hint}>
                {t('manualCoefficientsHint')}
              </span>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.checkboxRow}>
                {(() => {
                  const { ref, ...rest } = register('includeQualificationStages');
                  return (
                    <BasicCheckbox
                      id="include-qualification-stages"
                      color="green"
                      disabled={isLoading}
                      innerRef={ref}
                      {...rest}
                    />
                  );
                })()}
                <label htmlFor="include-qualification-stages" className={styles.label}>
                  {t('includeQualificationStagesLabel')}
                </label>
              </div>
              <span className={styles.hint}>
                {t('includeQualificationStagesHint')}
              </span>
            </div>
          </div>

          <div className={styles.actions}>
            {onCancel && (
              <button
                type="button"
                className="btn btn--sm btn--outlined"
                onClick={onCancel}
                disabled={isLoading}
              >
                {tCommon('cancel')}
              </button>
            )}
            <button type="submit" className="btn btn--sm" disabled={isLoading}>
              {tCommon('save')}
            </button>
          </div>
        </form>
      </FormCard>

      {isHolder && (
        <div className={styles.dangerZone}>
          <h4 className={styles.dangerTitle}>{t('dangerZone')}</h4>
          {!showArchiveConfirm ? (
            <button
              type="button"
              className={classNames('btn btn--sm btn--outlined', styles.dangerBtn)}
              onClick={() => setShowArchiveConfirm(true)}
              data-testid="archive-tournament-btn"
            >
              {t('archiveTournament')}
            </button>
          ) : (
            <div className={styles.archiveConfirm}>
              <p className={styles.archiveWarning}>
                {t('archiveWarning')}
              </p>
              <div className={styles.archiveActions}>
                <button
                  type="button"
                  className="btn btn--sm btn--outlined"
                  onClick={() => setShowArchiveConfirm(false)}
                  disabled={isArchiving}
                >
                  {tCommon('cancel')}
                </button>
                <button
                  type="button"
                  className={classNames('btn btn--sm', styles.dangerBtn)}
                  onClick={handleArchive}
                  disabled={isArchiving}
                  data-testid="confirm-archive-btn"
                >
                  {isArchiving ? t('archiving') : t('confirmArchive')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TournamentSettingsForm;
